import { Link } from "react-router-dom";
import { ArrowRight, Check, Handshake, FileCheck, Eye, Gift, ClipboardCheck, Sparkles, Info } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { forfaits, abonnements, missions, kits, methode, bonASavoir } from "@/data/tarifs";

const commitments = [
  { icon: Handshake, text: "Sans engagement de durée" },
  { icon: FileCheck, text: "Prix fixes, annoncés avant de commencer" },
  { icon: Eye, text: "Rien de facturé sans votre accord écrit" },
];

const Cta = ({ children = "Prendre rendez-vous", dark, testId, offer }) => (
  <Link
    to={offer ? `/rendez-vous?offre=${encodeURIComponent(offer)}` : "/rendez-vous"}
    data-testid={testId}
    className={`group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 ${
      dark ? "bg-argia-red text-white hover:bg-argia-redDark" : "border border-argia-ink text-argia-ink hover:bg-argia-ink hover:text-white"
    }`}
  >
    {children}
    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

const Heading = ({ label, title, text, light }) => (
  <Reveal>
    <SectionLabel>{label}</SectionLabel>
    <h2 className={`mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl ${light ? "text-white" : "text-argia-ink"}`}>{title}</h2>
    {text && <p className={`mt-4 max-w-2xl text-base leading-relaxed ${light ? "text-white/70" : "text-argia-muted"}`}>{text}</p>}
  </Reveal>
);

const Price = ({ price, period, accent }) => (
  <div className="flex flex-wrap items-baseline gap-x-2">
    <span className={`font-display text-3xl font-bold ${accent || "text-argia-red"}`}>{price}</span>
    <span className="text-xs text-argia-muted">{period}</span>
  </div>
);

const Start = () => (
  <section className="bg-argia-stone py-20" data-testid="tarifs-start">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Heading label="On commence toujours par là" title="Deux premières étapes, sans risque" />
      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="flex h-full flex-col rounded-2xl border border-[#2C2A29]/10 bg-white p-8" data-testid="plan-decouverte">
            <Gift size={26} strokeWidth={1.5} className="text-argia-red" />
            <h3 className="mt-5 font-display text-2xl font-bold text-argia-ink">Rendez-vous découverte</h3>
            <Price price="Offert" period="30 minutes · en présentiel ou à distance" />
            <p className="mt-4 flex-1 text-sm leading-relaxed text-argia-muted">
              Un premier échange pour comprendre votre situation, ce qui vous prend le plus de temps, et vérifier que je suis la bonne personne pour vous aider. Si la réponse est non, je vous le dirai.
            </p>
            <div className="mt-6"><Cta dark testId="plan-cta-decouverte">Réserver mon créneau</Cta></div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="flex h-full flex-col rounded-2xl bg-argia-ink p-8 text-white" data-testid="plan-audit">
            <ClipboardCheck size={26} strokeWidth={1.5} className="text-argia-gold" />
            <h3 className="mt-5 font-display text-2xl font-bold">L'audit Clarté</h3>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-display text-3xl font-bold text-argia-gold">150 € HT</span>
              <span className="text-xs text-white/60">· offert pour toute souscription d'un forfait mensuel dans les 30 jours, poursuivi au moins trois mois</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              Deux heures chez vous pour faire le tour de votre administratif : ce qui vous prend du temps, ce qui traîne, ce qui risque de passer à travers. Vous recevez sous 48 heures un rapport écrit avec un plan d'action chiffré.
            </p>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">
              Vous repartez avec un diagnostic clair, même si vous ne donnez pas suite : l'audit vous est alors facturé 150 € et nous en restons là. Si vous souscrivez un forfait, il vous est offert et devient définitivement acquis au bout de trois mois.
            </p>
            <div className="mt-6"><Cta dark testId="plan-cta-audit" offer="Audit Clarté">Demander un audit</Cta></div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Forfaits = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="tarifs-forfaits">
    <Heading
      label="Formules mensuelles"
      title="Une présence régulière, à votre rythme"
      text="Vous choisissez un volume d'heures, il est à vous : vous l'utilisez comme vous le souhaitez dans le mois. Sans engagement de durée, avec un préavis d'un mois."
    />
    <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
      {forfaits.map((p, i) => (
        <Reveal key={p.name} delay={i * 0.08}>
          <div
            data-testid={`forfait-${p.name.toLowerCase()}`}
            className={`relative flex h-full flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
              p.highlight ? "bg-argia-ink text-white shadow-xl" : "border border-[#2C2A29]/10 bg-white hover:shadow-lg"
            }`}
          >
            {p.highlight && (
              <span className="absolute -top-3 left-8 rounded-full bg-argia-red px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-white">Le plus choisi</span>
            )}
            <h3 className={`font-display text-2xl font-bold ${p.highlight ? "text-white" : "text-argia-ink"}`}>{p.name}</h3>
            <p className={`mt-2 min-h-[3.75rem] text-sm leading-relaxed ${p.highlight ? "text-white/70" : "text-argia-muted"}`}>{p.target}</p>
            <p className={`mt-4 text-xs font-semibold uppercase tracking-[0.2em] ${p.highlight ? "text-argia-gold" : "text-argia-red"}`}>{p.hours}</p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className={`font-display text-4xl font-bold ${p.highlight ? "text-argia-gold" : "text-argia-ink"}`}>{p.price}</span>
              <span className={`text-xs ${p.highlight ? "text-white/60" : "text-argia-muted"}`}>HT / mois</span>
            </div>
            <ul className="mt-6 flex-1 space-y-2.5">
              {p.features.map((f) => (
                <li key={f} className={`flex items-start gap-2.5 text-sm ${p.highlight ? "text-white/85" : "text-argia-ink"}`}>
                  <Check size={16} strokeWidth={2} className={`mt-0.5 shrink-0 ${p.highlight ? "text-argia-gold" : "text-argia-red"}`} />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-8"><Cta dark={p.highlight} testId={`forfait-cta-${p.name.toLowerCase()}`} offer={`Forfait ${p.name}`}>En discuter</Cta></div>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-dashed border-argia-red/40 bg-argia-stone p-8 md:flex-row md:items-center md:justify-between" data-testid="forfait-sur-mesure">
        <div>
          <div className="flex items-center gap-2 text-argia-red"><Sparkles size={18} strokeWidth={1.5} /><h3 className="font-display text-xl font-bold text-argia-ink">Forfait sur mesure</h3></div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-argia-muted">
            Aucun forfait ne vous convient, vous dépassez 28 heures par mois ou vous avez un besoin particulier ? Nous établissons ensemble une proposition sur mesure, après un premier échange offert.
            Un forfait Sérénité représente moins qu'un mi-temps une fois les charges comptées, sans recrutement, sans congés à remplacer et sans matériel à fournir.
          </p>
        </div>
        <Cta testId="forfait-cta-sur-mesure" offer="Forfait sur mesure">Demander une proposition</Cta>
      </div>
    </Reveal>
  </section>
);

const Abonnements = () => (
  <section className="bg-argia-stone py-20" data-testid="tarifs-abonnements">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Heading
        label="Petits budgets"
        title="Si vous n'avez pas encore besoin d'un forfait"
        text="Trois abonnements légers, sans engagement, pour ne rien laisser passer même quand vous faites tout vous-même."
      />
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {abonnements.map((a, i) => (
          <Reveal key={a.name} delay={i * 0.08}>
            <div className="flex h-full flex-col rounded-2xl border border-[#2C2A29]/10 bg-white p-8" data-testid={`abonnement-${i}`}>
              <h3 className="font-display text-xl font-bold text-argia-ink">{a.name}</h3>
              <div className="mt-3"><Price price={a.price} period={a.period} /></div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-argia-muted">{a.text}</p>
              <div className="mt-5"><Cta testId={`abonnement-cta-${i}`} offer={a.name}>Souscrire</Cta></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Missions = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="tarifs-missions">
    <Heading
      label="Missions ponctuelles"
      title="Pour un besoin précis, sans abonnement"
      text="Chaque mission est annoncée à prix fixe. Vous savez ce que vous payez avant que nous commencions."
    />
    <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {missions.map((m, i) => (
        <Reveal key={m.name} delay={(i % 3) * 0.06}>
          <div className="flex h-full flex-col rounded-2xl border border-[#2C2A29]/10 bg-white p-7 transition-shadow duration-300 hover:shadow-lg" data-testid={`mission-${i}`}>
            <h3 className="font-display text-lg font-bold text-argia-ink">{m.name}</h3>
            <div className="mt-2"><Price price={m.price} period={m.period} /></div>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-argia-muted">{m.text}</p>
            {m.note && <p className="mt-3 rounded-lg bg-argia-stone px-3 py-2 text-xs leading-relaxed text-argia-ink">{m.note}</p>}
            <div className="mt-5"><Cta testId={`mission-cta-${i}`} offer={m.name}>Réserver cette mission</Cta></div>
          </div>
        </Reveal>
      ))}
      <Reveal delay={0.1}>
        <div className="flex h-full flex-col rounded-2xl border border-dashed border-argia-red/40 bg-argia-stone p-7" data-testid="mission-sur-devis">
          <div className="flex items-center gap-2 text-argia-red"><Sparkles size={18} strokeWidth={1.5} /><h3 className="font-display text-lg font-bold text-argia-ink">Mission sur devis</h3></div>
          <Price price="Sur devis" period="gratuit et détaillé" />
          <p className="mt-3 flex-1 text-sm leading-relaxed text-argia-muted">
            Remise à plat d'un classement, dossier spécifique, projet particulier : si votre besoin n'entre dans aucune mission ci-dessus, nous définissons ensemble le périmètre et je vous remets un devis à prix fixe.
          </p>
          <div className="mt-5"><Cta testId="mission-cta-devis" offer="Mission sur devis">Demander un devis</Cta></div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Kits = () => (
  <section className="bg-argia-ink py-20" data-testid="tarifs-kits">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Heading
        light
        label="À faire soi-même"
        title="Mes kits, si vous préférez vous débrouiller"
        text="Le prix du kit est déduit du pack correspondant si vous décidez ensuite de me confier la mission, dans les 60 jours."
      />
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {kits.map((k, i) => (
          <Reveal key={k.name} delay={i * 0.08}>
            <div className="flex h-full flex-col rounded-2xl border border-white/10 p-8" data-testid={`kit-${i}`}>
              <h3 className="font-display text-xl font-bold text-white">{k.name}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold text-argia-gold">{k.price}</span>
                <span className="text-xs text-white/60">TTC</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/70">{k.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-8 text-sm text-white/60">Pour commander un kit, contactez-moi : <a href="mailto:a.violet@aadprox.com" className="text-argia-gold hover:text-white" data-testid="kits-email">a.violet@aadprox.com</a></p>
      </Reveal>
    </div>
  </section>
);

const Methode = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="tarifs-methode">
    <Heading label="La méthode" title="Comment cela se passe" />
    <div className="mt-10 grid gap-8 md:grid-cols-3">
      {methode.map((m, i) => (
        <Reveal key={m.title} delay={i * 0.1}>
          <span className="font-display text-5xl font-bold text-argia-gold">{i + 1}</span>
          <h3 className="mt-3 font-display text-xl font-bold text-argia-ink">{m.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-argia-muted">{m.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const BonASavoir = () => (
  <section className="bg-argia-stone py-20" data-testid="tarifs-bon-a-savoir">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Heading label="Bon à savoir" title="Ce qu'il faut retenir" />
      <ul className="mt-10 grid gap-4 md:grid-cols-2">
        {bonASavoir.map((b, i) => (
          <Reveal key={b} delay={(i % 2) * 0.06}>
            <li className="flex items-start gap-3 rounded-xl bg-white p-5 text-sm leading-relaxed text-argia-ink">
              <Info size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-argia-red" />
              {b}
            </li>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export default function Tarifs() {
  return (
    <div data-testid="tarifs-page">
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-16 lg:px-10 lg:pt-24">
        <Reveal>
          <SectionLabel>Tarifs 2026 – 2027</SectionLabel>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="tarifs-title">
            Votre administratif, tenu à jour, <span className="italic text-argia-red">sans que vous ayez à y penser</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-argia-muted md:text-lg">
            Vous êtes artisan, commerçant, indépendant ou dirigeant d'une petite structure. Vous passez vos soirées sur des tâches qui ne font pas la valeur de votre travail. Voici ce que je prends en charge, et ce que cela coûte.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4 rounded-2xl bg-argia-stone px-8 py-6">
            {commitments.map((c) => (
              <div key={c.text} className="flex items-center gap-2.5 text-sm font-medium text-argia-ink">
                <c.icon size={18} strokeWidth={1.5} className="text-argia-red" />
                {c.text}
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <Start />
      <Forfaits />
      <Abonnements />
      <Missions />
      <Kits />
      <Methode />
      <BonASavoir />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="tarifs-cta">
        <Reveal>
          <div className="rounded-2xl bg-argia-ink p-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-argia-gold">Premier échange offert</p>
            <h2 className="mt-4 font-display text-2xl font-bold text-white md:text-3xl">Dites-moi ce qui vous prend le plus de temps</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Trente minutes suffisent pour savoir si je peux vous aider, et par quoi commencer. Si la réponse est non, je vous le dirai.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
              <Cta dark testId="tarifs-cta-rdv">Prendre rendez-vous</Cta>
              <a href="tel:+33633837456" data-testid="tarifs-cta-phone" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-argia-ink">06 33 83 74 56</a>
            </div>
            <p className="mt-8 text-xs text-white/50">Tarifs en vigueur au 1er octobre 2026, révisables annuellement. Prix hors taxes, TVA 20 % en sus (kits TTC).</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
